import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import type { BasemapConfig } from "@/types/layers";
import {
  TbPlus,
  TbEdit,
  TbTrash,
  TbSearch,
  TbGripVertical,
  TbWorld,
} from "react-icons/tb";
import * as Label from "@radix-ui/react-label";
import { useBasemaps } from "@/hooks/useBasemaps";
import { useAuth } from "@/hooks/useAuth";
import { ConfirmationModal } from "@/components/ui";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface BasemapManagerProps {
  error?: string;
  onFormStateChange?: (
    isFormOpen: boolean,
    editingBasemap: BasemapConfig | null
  ) => void;
  onBackRef?: React.MutableRefObject<(() => void) | undefined>;
}

interface BasemapFormData {
  key: string;
  label: string;
  type: "raster" | "vector";
  style_url: string;
  attribution?: string;
  is_visible?: boolean;
  display_order?: number;
}

// Sortable basemap row component
interface SortableBasemapRowProps {
  basemap: BasemapConfig;
  onEdit: (basemap: BasemapConfig) => void;
  onDelete: (key: string) => void;
  onToggleVisibility: (key: string, isVisible: boolean) => void;
  isAdmin: boolean;
}

const SortableBasemapRow: React.FC<SortableBasemapRowProps> = ({
  basemap,
  onEdit,
  onDelete,
  isAdmin,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: basemap.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const styleUrl =
    basemap.type === "vector"
      ? typeof basemap.styleUrl === "string"
        ? basemap.styleUrl
        : "Inline Style"
      : basemap.url;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex justify-between items-start self-stretch p-4 rounded-2xl border border-[#F3F4F6] outline-1 outline-[#F3F4F6] -outline-offset-1 hover:bg-gray-50 transition-colors"
    >
      <div className="flex flex-col w-full justify-start items-start gap-3">
        {/* First section: Basemap name and status badges */}
        <div className="flex flex-col w-full justify-start items-start gap-2">
          <div className="flex flex-row justify-between w-full items-center gap-2.5">
            <div className="flex justify-start items-center gap-2.5">
              {/* Drag handle */}
              {isAdmin && (
                <div
                  {...attributes}
                  {...listeners}
                  className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
                  title="Drag to reorder"
                >
                  <TbGripVertical className="w-4 h-4 text-gray-400" />
                </div>
              )}
              <TbWorld className="w-5 h-5 text-[#374795]" />
              <div className="text-[#444444] text-[16px] font-['Open_Sans'] font-bold leading-[22.4px]">
                {basemap.label}
              </div>
              {/* Visibility Badge */}
              <div
                className={`px-2.5 py-[3px] pb-[5px] pl-2.5 pr-2.5 rounded-[20px] flex justify-center items-center gap-2 ${
                  basemap.isVisible
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                <div className="text-[12px] font-['Open_Sans'] font-semibold leading-[15.6px]">
                  {basemap.isVisible ? "Visible" : "Hidden"}
                </div>
              </div>
              {/* Type Badge */}
              <div
                className={`px-2.5 py-[3px] pb-[5px] pl-2.5 pr-2.5 rounded-[20px] flex justify-center items-center gap-2 ${
                  basemap.type === "vector"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-purple-100 text-purple-800"
                }`}
              >
                <div className="text-center text-[12px] font-['Open_Sans'] font-semibold leading-[15.6px]">
                  {basemap.type === "vector" ? "Vector" : "Raster"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              {/* Visibility Toggle */}
              {/* {isAdmin && (
                <div
                  onClick={() =>
                    onToggleVisibility(basemap.key, !(basemap.isVisible ?? true))
                  }
                  className="w-9 h-9 rounded-full border border-[#F3F4F6] bg-white flex items-center cursor-pointer justify-center hover:bg-gray-50 transition-colors"
                  title={basemap.isVisible ? "Hide" : "Show"}
                >
                  {basemap.isVisible ? (
                    <TbEye size={14} className="text-green-600" />
                  ) : (
                    <TbEyeOff size={14} className="text-[#828282]" />
                  )}
                </div>
              )} */}
              {/* Action icons */}
              {isAdmin && (
                <div className="flex justify-end items-start gap-2 shrink-0">
                  <div
                    onClick={() => onEdit(basemap)}
                    className="w-9 h-9 rounded-full border border-[#F3F4F6] bg-white flex items-center cursor-pointer justify-center hover:bg-gray-50 transition-colors"
                    title="Edit Basemap"
                  >
                    <TbEdit size={14} className="text-[#828282]" />
                  </div>
                  <div
                    onClick={() => onDelete(basemap.key)}
                    className="w-9 h-9 rounded-full border border-[#F3F4F6] bg-white flex items-center cursor-pointer justify-center hover:bg-red-50 transition-colors"
                    title="Delete Basemap"
                  >
                    <TbTrash size={14} className="text-red-500" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Second section: Key */}
        <div className="flex flex-col w-full justify-start items-start gap-1">
          <div className="text-[#828282] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px]">
            Basemap Key
          </div>
          <div className="text-[#444444] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px]">
            {basemap.key}
          </div>
        </div>

        {/* Third section: Style URL */}
        <div className="flex flex-col w-full justify-start items-start gap-1">
          <div className="text-[#828282] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px]">
            Style URL
          </div>
          <div className="text-[#444444] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px] break-all">
            {styleUrl}
          </div>
        </div>

        {/* Fourth section: Attribution (if exists) */}
        {basemap.attribution && (
          <div className="flex flex-col w-full justify-start items-start gap-1">
            <div className="text-[#828282] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px]">
              Attribution
            </div>
            <div className="text-[#444444] text-[12px] font-['Open_Sans'] font-medium leading-[15.6px]">
              {basemap.attribution}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const BasemapManager: React.FC<BasemapManagerProps> = ({
  error,
  onFormStateChange,
  onBackRef,
}) => {
  const { isAdmin } = useAuth();
  const {
    basemaps,
    isLoading,
    createMutation,
    updateMutation,
    deleteMutation,
    updateVisibilityMutation,
    reorderMutation,
  } = useBasemaps();

  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingBasemap, setEditingBasemap] = useState<BasemapConfig | null>(
    null
  );
  const [formData, setFormData] = useState<BasemapFormData>({
    key: "",
    label: "",
    type: "vector",
    style_url: "",
    attribution: "",
    is_visible: true,
    display_order: 0,
  });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [deleteConfirmKey, setDeleteConfirmKey] = useState<string | null>(null);

  // Notify parent when form state changes
  useEffect(() => {
    onFormStateChange?.(showCreateForm || !!editingBasemap, editingBasemap);
  }, [showCreateForm, editingBasemap, onFormStateChange]);

  // Expose reset function to parent via ref
  useEffect(() => {
    if (onBackRef) {
      onBackRef.current = () => {
        setShowCreateForm(false);
        setEditingBasemap(null);
        setFormData({
          key: "",
          label: "",
          type: "vector",
          style_url: "",
          attribution: "",
          is_visible: true,
          display_order: 0,
        });
        setValidationError(null);
      };
    }
  }, [onBackRef]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter basemaps by search term
  const filteredBasemaps = useMemo(() => {
    if (!searchTerm.trim()) return basemaps;
    const lowerSearch = searchTerm.toLowerCase();
    return basemaps.filter(
      (b) =>
        b.label.toLowerCase().includes(lowerSearch) ||
        b.key.toLowerCase().includes(lowerSearch) ||
        b.type.toLowerCase().includes(lowerSearch)
    );
  }, [basemaps, searchTerm]);

  // Handle drag end for reordering
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = basemaps.findIndex((b) => b.key === active.id);
    const newIndex = basemaps.findIndex((b) => b.key === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = arrayMove(basemaps, oldIndex, newIndex);
      const updates = reordered.map((b, idx) => ({
        key: b.key,
        display_order: idx,
      }));

      reorderMutation.mutate(updates, {
        onError: (error) => {
          console.error("Failed to reorder basemaps:", error);
        },
      });
    }
  };

  const handleStartCreate = () => {
    if (!isAdmin) {
      toast.error("Only administrators can create basemaps.");
      return;
    }
    setFormData({
      key: "",
      label: "",
      type: "vector",
      style_url: "",
      attribution: "",
      is_visible: true,
      display_order: basemaps.length,
    });
    setValidationError(null);
    setShowCreateForm(true);
  };

  const handleStartEdit = (basemap: BasemapConfig) => {
    if (!isAdmin) {
      toast.error("Only administrators can edit basemaps.");
      return;
    }
    const styleUrl =
      basemap.type === "vector"
        ? typeof basemap.styleUrl === "string"
          ? basemap.styleUrl
          : ""
        : basemap.url;

    setFormData({
      key: basemap.key,
      label: basemap.label,
      type: basemap.type,
      style_url: styleUrl,
      attribution: basemap.attribution || "",
      is_visible: basemap.isVisible ?? true,
      display_order: basemap.displayOrder ?? 0,
    });
    setEditingBasemap(basemap);
    setValidationError(null);
    setShowCreateForm(true);
  };

  const handleCancelForm = () => {
    setShowCreateForm(false);
    setEditingBasemap(null);
    setFormData({
      key: "",
      label: "",
      type: "vector",
      style_url: "",
      attribution: "",
      is_visible: true,
      display_order: 0,
    });
    setValidationError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.key.trim()) {
      setValidationError("Basemap key is required");
      return false;
    }
    if (formData.key.trim().length > 50) {
      setValidationError("Basemap key must be 50 characters or less");
      return false;
    }
    if (!formData.label.trim()) {
      setValidationError("Basemap label is required");
      return false;
    }
    if (formData.label.trim().length > 50) {
      setValidationError("Basemap label must be 50 characters or less");
      return false;
    }
    if (!formData.style_url.trim()) {
      setValidationError("Style URL is required");
      return false;
    }
    if (formData.style_url.trim().length > 500) {
      setValidationError("Style URL must be 500 characters or less");
      return false;
    }
    // Check for duplicate keys (only when creating)
    if (
      !editingBasemap &&
      basemaps.some((b) => b.key === formData.key.trim())
    ) {
      setValidationError("A basemap with this key already exists");
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submissionData = {
      label: formData.label.trim(),
      type: formData.type,
      style_url: formData.style_url.trim(),
      attribution: formData.attribution?.trim() || undefined,
      is_visible: formData.is_visible,
      display_order: formData.display_order,
    };

    if (editingBasemap) {
      updateMutation.mutate(
        {
          key: editingBasemap.key,
          data: submissionData,
        },
        {
          onSuccess: () => {
            handleCancelForm();
          },
          onError: (error) => {
            console.error("Failed to update basemap:", error);
            setValidationError("Failed to update basemap. Please try again.");
          },
        }
      );
    } else {
      // When creating, include the key
      const createData = {
        key: formData.key.trim(),
        ...submissionData,
      };
      createMutation.mutate(createData, {
        onSuccess: () => {
          handleCancelForm();
        },
        onError: (error) => {
          console.error("Failed to create basemap:", error);
          setValidationError("Failed to create basemap. Please try again.");
        },
      });
    }
  };

  const handleToggleVisibility = async (key: string, isVisible: boolean) => {
    updateVisibilityMutation.mutate(
      { key, isVisible },
      {
        onError: (error) => {
          console.error("Failed to toggle visibility:", error);
        },
      }
    );
  };

  const handleDeleteClick = (key: string) => {
    setDeleteConfirmKey(key);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmKey) return;
    deleteMutation.mutate(deleteConfirmKey, {
      onSuccess: () => {
        setDeleteConfirmKey(null);
      },
      onError: (error) => {
        console.error("Failed to delete basemap:", error);
        setDeleteConfirmKey(null);
      },
    });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmKey(null);
  };

  // Get the basemap being deleted for the modal description
  const deletingBasemap = useMemo(
    () => basemaps.find((b) => b.key === deleteConfirmKey),
    [basemaps, deleteConfirmKey]
  );

  return (
    <div className="w-full h-full">
      {/* Create/Edit Form */}
      {showCreateForm ? (
        <>
          <div className="flex flex-col items-start w-full overflow-y-auto min-h-[400px] max-h-[calc(100vh-380px)] p-6 rounded-[10px] border border-gray-100">
            <div className="space-y-6 mb-8 w-full">
              {validationError && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {validationError}
                </div>
              )}

              <div>
                <Label.Root
                  htmlFor="basemap-key"
                  className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                >
                  Basemap Key <span className="text-red-500">*</span>
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <input
                    id="basemap-key"
                    type="text"
                    value={formData.key}
                    onChange={(e) =>
                      setFormData({ ...formData, key: e.target.value })
                    }
                    disabled={!!editingBasemap}
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-normal placeholder-[#828282] border-none outline-none w-full disabled:opacity-50"
                    placeholder="e.g., osm-streets"
                    maxLength={50}
                  />
                </div>
                <div className="flex justify-between items-center mt-1">
                  <p className="text-[12px] text-[#828282]">
                    Unique identifier for the basemap (cannot be changed after
                    creation)
                  </p>
                  <span
                    className={`text-[12px] font-medium ${formData.key.length >= 50 ? "text-red-500" : "text-[#828282]"}`}
                  >
                    {formData.key.length}/50
                  </span>
                </div>
              </div>

              <div>
                <Label.Root
                  htmlFor="basemap-label"
                  className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                >
                  Basemap Label <span className="text-red-500">*</span>
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <input
                    id="basemap-label"
                    type="text"
                    value={formData.label}
                    onChange={(e) =>
                      setFormData({ ...formData, label: e.target.value })
                    }
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-normal placeholder-[#828282] border-none outline-none w-full"
                    placeholder="e.g., OpenStreetMap Streets"
                    maxLength={50}
                  />
                </div>
                <div className="flex justify-end mt-1">
                  <span
                    className={`text-[12px] font-medium ${formData.label.length >= 50 ? "text-red-500" : "text-[#828282]"}`}
                  >
                    {formData.label.length}/50
                  </span>
                </div>
              </div>

              <div>
                <Label.Root
                  htmlFor="basemap-type"
                  className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                >
                  Basemap Type <span className="text-red-500">*</span>
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <select
                    id="basemap-type"
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        type: e.target.value as "raster" | "vector",
                      })
                    }
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-normal border-none outline-none w-full"
                  >
                    <option value="vector">Vector</option>
                    <option value="raster">Raster</option>
                  </select>
                </div>
                <p className="text-[12px] text-[#828282] mt-1">
                  <strong>Vector:</strong> Smooth rendering at any zoom level,
                  MapLibre style JSON
                  <br />
                  <strong>Raster:</strong> Tile-based imagery, XYZ tile URL
                </p>
              </div>

              <div>
                <Label.Root
                  htmlFor="basemap-style-url"
                  className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                >
                  Style URL <span className="text-red-500">*</span>
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <input
                    id="basemap-style-url"
                    type="text"
                    value={formData.style_url}
                    onChange={(e) =>
                      setFormData({ ...formData, style_url: e.target.value })
                    }
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-normal placeholder-[#828282] border-none outline-none w-full"
                    placeholder={
                      formData.type === "vector"
                        ? "https://example.com/style.json"
                        : "https://example.com/tiles/{z}/{x}/{y}.png"
                    }
                    maxLength={500}
                  />
                </div>
                <p className="text-[12px] text-[#828282] mt-1">
                  {formData.type === "vector"
                    ? "URL to MapLibre style JSON or inline style object"
                    : "XYZ tile URL with {z}, {x}, {y} placeholders"}
                </p>
              </div>

              <div>
                <Label.Root
                  htmlFor="basemap-attribution"
                  className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                >
                  Attribution
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <input
                    id="basemap-attribution"
                    type="text"
                    value={formData.attribution}
                    onChange={(e) =>
                      setFormData({ ...formData, attribution: e.target.value })
                    }
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-normal placeholder-[#828282] border-none outline-none w-full"
                    placeholder="© Map Provider"
                    maxLength={200}
                  />
                </div>
                <p className="text-[12px] text-[#828282] mt-1">
                  Copyright or attribution text for the basemap
                </p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  id="basemap-visible"
                  type="checkbox"
                  checked={formData.is_visible}
                  onChange={(e) =>
                    setFormData({ ...formData, is_visible: e.target.checked })
                  }
                  className="w-4 h-4 rounded border border-gray-300"
                />
                <Label.Root
                  htmlFor="basemap-visible"
                  className="text-[14px] font-medium text-[#444444] cursor-pointer"
                >
                  Visible by default
                </Label.Root>
              </div>
            </div>
          </div>
          <div className="flex gap-6 w-full">
            <button
              onClick={handleSubmit}
              className="self-stretch w-full px-6 py-3 rounded-[10px] gap-2.5 text-white bg-[#374795] hover:bg-[#132477] cursor-pointer text-sm font-semibold font-['Open_Sans'] leading-tight"
            >
              {editingBasemap ? "Update Basemap" : "Create Basemap"}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col items-start w-full overflow-y-auto max-h-[calc(100vh-380px)]">
            <div className="space-y-4 w-full">
              {error && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}

              {/* Search Bar */}
              <div className="flex flex-col items-start gap-1.5 w-full rounded-lg">
                <div className="flex p-[13px_14px] items-center gap-4 w-full rounded-[10px] bg-[#F3F4F6]">
                  <div className="flex justify-center items-center gap-2 flex-1">
                    <TbSearch className="w-4 h-4 text-[#828282]" />
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-transparent border-none outline-none text-[#828282] font-['Open_Sans'] text-[12px] font-medium leading-[18px] flex-1 placeholder:text-[#828282]"
                    />
                  </div>
                </div>
              </div>

              {/* Basemap List */}
              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredBasemaps.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm
                    ? "No basemaps found matching your search."
                    : "No basemaps created yet. Create your first basemap!"}
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <div className="space-y-3">
                    <SortableContext
                      items={filteredBasemaps.map((b) => b.key)}
                      strategy={verticalListSortingStrategy}
                    >
                      {filteredBasemaps.map((basemap) => (
                        <SortableBasemapRow
                          key={basemap.key}
                          basemap={basemap}
                          onEdit={handleStartEdit}
                          onDelete={handleDeleteClick}
                          onToggleVisibility={handleToggleVisibility}
                          isAdmin={isAdmin}
                        />
                      ))}
                    </SortableContext>
                  </div>
                </DndContext>
              )}
            </div>
          </div>

          {/* Add New Basemap Button */}
          {isAdmin && (
            <div className="flex gap-6 w-full mt-4">
              <button
                onClick={handleStartCreate}
                className="self-stretch w-full px-6 py-3 rounded-[10px] gap-2.5 text-white bg-[#374795] hover:bg-[#132477] cursor-pointer text-sm font-semibold font-['Open_Sans'] leading-tight flex items-center justify-center"
              >
                <TbPlus className="w-4 h-4" />
                Add New Basemap
              </button>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={!!deleteConfirmKey}
        onOpenChange={(open) => !open && handleCancelDelete()}
        title="Delete Basemap"
        description={`Are you sure you want to delete "${deletingBasemap?.label || "this basemap"}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
